export type QueryParams =
  | Record<string, string | number | boolean | null | undefined>
  | Array<[string, string | number | boolean | null | undefined]>

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ApiClientOptions {
  baseUrl?: string
  forceFullApiPath?: boolean
  credentials?: RequestCredentials
  retryCount?: number
  retryDelayMs?: number
  cacheBustParam?: string
  onError?: (response: Response, rawData: any) => void | Promise<void>
}

export class ApiError extends Error {
  public status: number
  public url: string
  public method: HttpMethod
  public raw: any

  constructor(params: {
    status: number
    url: string
    method: HttpMethod
    raw: any
    message?: string
  }) {
    super(params.message ?? `API Error ${params.status}`)
    this.name = 'ApiError'
    this.status = params.status
    this.url = params.url
    this.method = params.method
    this.raw = params.raw
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

function isRetryableStatus(status: number) {
  return status === 408 || status === 429 || (status >= 500 && status <= 599)
}

function normalizeParams(params?: QueryParams): Array<[string, string]> {
  if (!params) return []

  const pairs: Array<[string, string]> = []
  if (Array.isArray(params)) {
    for (const [k, v] of params) {
      if (v === undefined || v === null) continue
      pairs.push([k, String(v)])
    }
  } else {
    for (const k of Object.keys(params)) {
      const v = (params as any)[k]
      if (v === undefined || v === null) continue
      pairs.push([k, String(v)])
    }
  }
  return pairs
}

function buildUrl(
  baseUrl: string | undefined,
  forceFullApiPath: boolean | undefined,
  url: string,
  query?: QueryParams,
  cacheBustParam?: string,
) {
  const pairs = normalizeParams(query)

  if (cacheBustParam) {
    pairs.push([cacheBustParam, String(Date.now())])
  }

  const qs = pairs.length ? `?${new URLSearchParams(pairs).toString()}` : ''

  const finalUrl =
    forceFullApiPath && baseUrl
      ? `${baseUrl.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`
      : url

  return `${finalUrl}${qs}`
}

async function parseBody(res: Response): Promise<any> {
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('application/json')) {
    const text = await res.text()
    return text ? JSON.parse(text) : null
  }
  if (
    ct.startsWith('text/') ||
    ct.includes('application/xml') ||
    ct.includes('application/xhtml+xml')
  ) {
    return await res.text()
  }
  return await res.blob()
}

function shouldRetryOnNetworkError(err: unknown) {
  return err instanceof TypeError
}

export function createApiClient(opts: ApiClientOptions = {}) {
  const config: Required<ApiClientOptions> = {
    baseUrl: opts.baseUrl ?? (import.meta as any).env?.VITE_API_SITE_URL ?? '',
    forceFullApiPath: opts.forceFullApiPath ?? false,
    credentials: opts.credentials ?? 'include',
    retryCount: opts.retryCount ?? 2,
    retryDelayMs: opts.retryDelayMs ?? 250,
    cacheBustParam: opts.cacheBustParam ?? 'chqfix',
    onError: opts.onError ?? (() => {}),
  }

  function setForceFullApiPath(value: boolean) {
    config.forceFullApiPath = value
  }

  async function request<T>(
    method: HttpMethod,
    url: string,
    options?: {
      query?: QueryParams
      body?: any
      headers?: HeadersInit
      signal?: AbortSignal
      retryCount?: number
      cacheBust?: boolean
    },
  ): Promise<T> {
    const retryLeft = options?.retryCount ?? config.retryCount
    const cacheBustParam = options?.cacheBust === false ? undefined : config.cacheBustParam

    const fullUrl = buildUrl(
      config.baseUrl,
      config.forceFullApiPath,
      url,
      options?.query,
      cacheBustParam,
    )

    const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData

    const headers: HeadersInit = {
      Accept: 'application/json',
      ...(options?.headers ?? {}),
    }

    const hasBody =
      method !== 'GET' && method !== 'DELETE'
        ? options?.body !== undefined
        : options?.body !== undefined
    if (hasBody && !isFormData && !(headers as any)['Content-Type']) {
      ;(headers as any)['Content-Type'] = 'application/json'
    }

    const init: RequestInit = {
      method,
      headers,
      credentials: config.credentials,
      signal: options?.signal,
    }

    if (hasBody) {
      init.body = isFormData ? options!.body : JSON.stringify(options!.body ?? {})
    }

    let res: Response | undefined

    try {
      res = await fetch(fullUrl, init)

      if (
        !res.ok &&
        isRetryableStatus(res.status) &&
        retryLeft > 0 &&
        res.status !== 401 &&
        res.status !== 403
      ) {
        await sleep(config.retryDelayMs)
        return await request<T>(method, url, { ...options, retryCount: retryLeft - 1 })
      }

      const data = await parseBody(res)

      if (!res.ok) {
        await config.onError(res, data)
        throw new ApiError({
          status: res.status,
          url: fullUrl,
          method,
          raw: data,
          message: res.statusText,
        })
      }

      return data as T
    } catch (err) {
      if (retryLeft > 0 && shouldRetryOnNetworkError(err)) {
        await sleep(config.retryDelayMs)
        return await request<T>(method, url, { ...options, retryCount: retryLeft - 1 })
      }
      throw err
    }
  }

  return {
    setForceFullApiPath,

    get<T>(
      url: string,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<T>('GET', url, {
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
      })
    },

    getBlob(
      url: string,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<Blob>('GET', url, {
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
      })
    },

    post<T>(
      url: string,
      body?: any,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<T>('POST', url, {
        body: body ?? {},
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
        cacheBust: false,
      })
    },

    put<T>(
      url: string,
      body?: any,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<T>('PUT', url, {
        body: body ?? {},
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
        cacheBust: false,
      })
    },

    patch<T>(
      url: string,
      body?: any,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<T>('PATCH', url, {
        body: body ?? {},
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
        cacheBust: false,
      })
    },

    delete<T>(
      url: string,
      body?: any,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<T>('DELETE', url, {
        body: body ?? undefined,
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
        cacheBust: false,
      })
    },

    sendFile<T>(
      url: string,
      formData: FormData,
      query?: QueryParams,
      options?: { signal?: AbortSignal; retryCount?: number },
    ) {
      return request<T>('POST', url, {
        body: formData,
        query,
        signal: options?.signal,
        retryCount: options?.retryCount,
        cacheBust: false,
      })
    },
  }
}
